import React, { useState, useEffect } from 'react'
import SpaceTheme from '../components/SpaceTheme'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Quiz as QuizIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { db } from '../firebase/config'
import { doc, getDoc, addDoc, collection } from 'firebase/firestore'
import toast from 'react-hot-toast'
import AdPlacement from '../components/AdPlacement'

const MiniQuizSolve = ({ quizId, quizTitle, quizDescription, timeLimit, onBack }) => {
  const { user } = useAuth()
  const [questions, setQuestions] = useState([])
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [userAnswers, setUserAnswers] = useState([])
  const [selectedAnswer, setSelectedAnswer] = useState('')
  const [loading, setLoading] = useState(true)
  const [showAnswerDialog, setShowAnswerDialog] = useState(false)
  const [answerFeedback, setAnswerFeedback] = useState({ isCorrect: false, correctAnswer: '' })
  const [showResultDialog, setShowResultDialog] = useState(false)
  const [finalScore, setFinalScore] = useState({ correct: 0, total: 0 })
  const [showInterstitialAd, setShowInterstitialAd] = useState(false)

  useEffect(() => {
    if (quizId) {
      loadQuiz()
    }
  }, [quizId])

  const loadQuiz = async () => {
    try {
      setLoading(true)
      
      // quizId'de slash varsa, ilk kısmını document ID olarak kullan (Android uygulamasıyla aynı)
      const documentId = quizId.includes('/') ? quizId.split('/')[0] : quizId
      
      const quizDoc = await getDoc(doc(db, 'miniQuizzes', documentId))
      
      if (quizDoc.exists()) {
        const quizData = quizDoc.data()
        const questionsData = quizData.questions || []
        
        if (questionsData.length > 0) {
          setQuestions(questionsData)
          setUserAnswers(new Array(questionsData.length).fill(''))
          setCurrentQuestionIndex(0)
          setSelectedAnswer('')
        } else {
          toast.error('Quiz soruları bulunamadı')
          onBack()
        }
      } else {
        toast.error('Quiz bulunamadı')
        onBack()
      }
    } catch (error) {
      console.error('Error loading quiz:', error)
      toast.error('Quiz yüklenemedi')
      onBack()
    } finally {
      setLoading(false)
    }
  }

  const handleAnswerChange = (event) => {
    setSelectedAnswer(event.target.value)
  }

  const handleNextQuestion = () => {
    if (!selectedAnswer) {
      toast.warning('Lütfen bir seçenek seçin')
      return
    }

    // Cevabı kaydet
    const newAnswers = [...userAnswers]
    newAnswers[currentQuestionIndex] = selectedAnswer
    setUserAnswers(newAnswers)

    // Doğru cevapla karşılaştır
    const currentQuestion = questions[currentQuestionIndex]
    const isCorrect = selectedAnswer === currentQuestion.correctAnswer
    
    setAnswerFeedback({
      isCorrect,
      correctAnswer: currentQuestion.correctAnswer
    })
    setShowAnswerDialog(true)
  }

  const handleAnswerDialogClose = () => {
    setShowAnswerDialog(false)
    
    if (currentQuestionIndex >= questions.length - 1) {
      // Quiz tamamlandı, sonuçları hesapla
      calculateResults()
    } else {
      // Sonraki soruya geç
      setCurrentQuestionIndex(currentQuestionIndex + 1)
      setSelectedAnswer('')
    }
  }

  const calculateResults = () => {
    let correctCount = 0
    for (let i = 0; i < questions.length; i++) {
      if (userAnswers[i] === questions[i].correctAnswer) {
        correctCount++
      }
    }
    
    setFinalScore({ correct: correctCount, total: questions.length })
    setShowResultDialog(true)
    
    // Sonucu Firestore'a kaydet (Android uygulamasıyla aynı)
    saveMiniQuizResult(correctCount, questions.length)
  }

  const saveMiniQuizResult = async (correct, total) => {
    try {
      const studentId = user.uid
      
      // Önce quiz'in teacherId'sini al (Android uygulamasıyla aynı)
      const documentId = quizId.includes('/') ? quizId.split('/')[0] : quizId
      const quizDoc = await getDoc(doc(db, 'miniQuizzes', documentId))
      
      if (quizDoc.exists()) {
        const quizData = quizDoc.data()
        const teacherId = quizData.teacherId
        const classId = quizData.classId
        const className = quizData.className
        
        const result = {
          quizId: quizId,
          studentId: studentId,
          teacherId: teacherId,
          classId: classId,
          className: className,
          score: correct,
          totalQuestions: total,
          completionDate: new Date(),
          userAnswers: userAnswers
        }
        
        await addDoc(collection(db, 'quizResults'), result)
        console.log('Quiz sonucu kaydedildi:', result)
      }
    } catch (error) {
      console.error('Error saving quiz result:', error)
      toast.error('Sonuç kaydedilirken hata oluştu')
    }
  }

  const handleQuizComplete = () => {
    setShowResultDialog(false)
    // Quiz tamamlandıktan sonra interstitial reklam göster
    setShowInterstitialAd(true)
    // 3 saniye sonra otomatik olarak kapat ve geri dön
    setTimeout(() => {
      setShowInterstitialAd(false)
      toast.success('Quiz başarıyla tamamlandı!')
      onBack()
    }, 3000)
  }

  if (loading) {
    return (
      <SpaceTheme>
        <Box sx={{ flexGrow: 1 }}>
          <AppBar position="static" sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}>
            <Toolbar>
              <IconButton edge="start" color="inherit" onClick={onBack} sx={{ mr: 2 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                {quizTitle || 'Quiz'}
              </Typography>
            </Toolbar>
          </AppBar>
          <Container maxWidth="md" sx={{ py: 3 }}>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <LinearProgress sx={{ mb: 2 }} />
              <Typography>Quiz yükleniyor...</Typography>
            </Box>
          </Container>
        </Box>
      </SpaceTheme>
    )
  }

  if (questions.length === 0) {
    return (
      <SpaceTheme>
        <Box sx={{ flexGrow: 1 }}>
          <AppBar position="static" sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}>
            <Toolbar>
              <IconButton edge="start" color="inherit" onClick={onBack} sx={{ mr: 2 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                Quiz Hatası
              </Typography>
            </Toolbar>
          </AppBar>
          <Container maxWidth="md" sx={{ py: 3 }}>
            <Alert severity="error">
              Quiz soruları yüklenemedi. Lütfen daha sonra tekrar deneyin.
            </Alert>
          </Container>
        </Box>
      </SpaceTheme>
    )
  }

  const currentQuestion = questions[currentQuestionIndex]
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100

  return (
    <SpaceTheme>
      <Box sx={{ flexGrow: 1 }}>
        {/* App Bar */}
        <AppBar position="static" sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
        }}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={onBack} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              {quizTitle}
            </Typography>
            <Typography variant="body2" sx={{ mr: 2 }}>
              {currentQuestionIndex + 1}/{questions.length}
            </Typography>
          </Toolbar>
        </AppBar>

        {/* Main Content */}
        <Container maxWidth="md" sx={{ py: 3 }}>
          {/* Progress Bar */}
          <Card sx={{ 
            mb: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          }}>
            <CardContent sx={{ pb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                İlerleme: {currentQuestionIndex + 1} / {questions.length}
              </Typography>
              <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
            </CardContent>
          </Card>

          {/* Question Card */}
          <Card sx={{ 
            mb: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                Soru {currentQuestionIndex + 1}
              </Typography>
              <Typography variant="h5" sx={{ mb: 3 }}>
                {currentQuestion.question}
              </Typography>

              <RadioGroup value={selectedAnswer} onChange={handleAnswerChange}>
                {/* Android uygulamasıyla aynı format: optionA, optionB, etc. */}
                {[
                  { key: 'A', value: currentQuestion.optionA },
                  { key: 'B', value: currentQuestion.optionB },
                  { key: 'C', value: currentQuestion.optionC },
                  { key: 'D', value: currentQuestion.optionD },
                  { key: 'E', value: currentQuestion.optionE }
                ].filter(option => option.value && option.value.trim()).map((option) => (
                  <FormControlLabel
                    key={option.key}
                    value={option.key}
                    control={<Radio />}
                    label={`${option.key}) ${option.value}`}
                    sx={{
                      mb: 1,
                      p: 1,
                      borderRadius: 2,
                      border: '1px solid rgba(0, 0, 0, 0.1)',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.05)'
                      }
                    }}
                  />
                ))}
              </RadioGroup>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  {quizDescription}
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleNextQuestion}
                  disabled={!selectedAnswer}
                  startIcon={<QuizIcon />}
                >
                  {currentQuestionIndex >= questions.length - 1 ? 'Tamamla' : 'Sonraki'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Container>

        {/* Answer Feedback Dialog */}
        <Dialog open={showAnswerDialog} onClose={handleAnswerDialogClose}>
          <DialogTitle>
            {answerFeedback.isCorrect ? '✅ Doğru!' : '❌ Yanlış!'}
          </DialogTitle>
          <DialogContent>
            <Typography>
              {answerFeedback.isCorrect 
                ? 'Tebrikler, doğru cevap verdiniz.' 
                : `Yanlış cevap! Doğru cevap: ${answerFeedback.correctAnswer}`
              }
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleAnswerDialogClose} variant="contained">
              Devam
            </Button>
          </DialogActions>
        </Dialog>

        {/* Final Result Dialog */}
        <Dialog open={showResultDialog} onClose={handleQuizComplete}>
          <DialogTitle>🎉 Quiz Tamamlandı!</DialogTitle>
          <DialogContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Sonucunuz: {finalScore.correct}/{finalScore.total}
            </Typography>
            <Typography variant="body1">
              Doğru cevap oranı: %{Math.round((finalScore.correct / finalScore.total) * 100)}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleQuizComplete} variant="contained">
              Tamam
            </Button>
          </DialogActions>
        </Dialog>

        {/* Interstitial Reklam */}
        {showInterstitialAd && (
          <AdPlacement 
            type="interstitial" 
            position="overlay" 
            pageType="quiz"
          />
        )}
      </Box>
    </SpaceTheme>
  )
}

export default MiniQuizSolve
